import React, { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useRef } from 'react';
import { TextField } from '@mui/material';

import { Input } from 'components/atoms';
import { POST_FILE } from 'queries';
import toastError from 'utils/toastError';
import toastSuccess from 'utils/toastSuccess';

import { MessageFileType } from 'queries/types/MessageFormType';
import { MessageResultType } from 'queries/types/MessageResultType';

import './FileType.scss';

type FileTypeProps = {
  submitForm: boolean;
  fileFormData: MessageFileType;
  setFileFormData: Dispatch<SetStateAction<MessageFileType>>;
  setFormValidated: Dispatch<SetStateAction<boolean>>;
  setSubmitForm: Dispatch<SetStateAction<boolean>>;
  setLoadingState: Dispatch<SetStateAction<boolean>>;
  setResultData: Dispatch<SetStateAction<MessageResultType | null>>;
};

const FileType: FC<FileTypeProps> = ({
  submitForm,
  fileFormData,
  setFileFormData,
  setFormValidated,
  setSubmitForm,
  setLoadingState,
  setResultData,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (fileFormData.file) setFormValidated(true);
    else setFormValidated(false);
  }, [fileFormData]);

  useEffect(() => {
    if (submitForm) {
      setSubmitForm(false);

      if (fileFormData.file) {
        const formDataToSend = new FormData();
        formDataToSend.append('file', fileFormData.file);

        if (fileFormData.name) {
          formDataToSend.append('sheet_name', fileFormData.name);
        }

        POST_FILE(formDataToSend).then((result) => {
          setLoadingState(false);
          if (result.success) {
            setResultData(result.data && 'data' in result.data ? result.data.data : null);
            toastSuccess(`Paraphrasing completed successfully!`);
          } else {
            setFormValidated(true);
            toastError(`Error while paraphrasing process!`);
          }
        });
      }
    }
  }, [submitForm]);

  const handleChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxFileSize = 200 * 1024 * 1024;

    if (file.size > maxFileSize) {
      toastError(`File size exceeds 200 MB!`);
      e.target.value = '';
      return;
    }

    setFileFormData({ ...fileFormData, file: file, fileName: file.name });
  };

  return (
    <div className="mf__file-type">
      <div className={'mf__file-type__file-box'}>
        <TextField
          type="file"
          variant="outlined"
          label="Upload an input excel file"
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleChangeFile(event)}
          inputRef={fileInputRef}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            '& input[type="file"]': {
              color: 'transparent',
              paddingBottom: '40px',
            },
            '& input[type="file"]::-webkit-file-upload-button': {
              color: 'transparent',
            },
          }}
        />
        <p className="mf__file-type__file-subText" onClick={() => fileInputRef.current?.click()}>
          {fileFormData.fileName ? fileFormData.fileName : 'Drag and drop file here'}
        </p>
        <p className="mf__file-type__file-text" onClick={() => fileInputRef.current?.click()}>
          Browse file
        </p>
        <p
          className="mf__file-type__file-placeholder"
          onClick={() => fileInputRef.current?.click()}
        >
          Limit 200MB per file
        </p>
      </div>
      <div className={'mf__manual-type__separator'} />
      <div className={'mf__file-type__name-box'}>
        <label>Sheet Name</label>
        <Input
          className={'mf__file-type__name'}
          value={fileFormData.name}
          onChange={({ target }) => setFileFormData({ ...fileFormData, name: target.value })}
          placeholder={'Input the name of the excel sheet...'}
        />
      </div>
    </div>
  );
};

export default FileType;
